import React from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';

const faqData = [
    {
        category: 'Getting Started',
        questions: [
            {
                q: 'What is APILoadTest?',
                a: 'APILoadTest is a cloud-based, no-code API load testing platform. It lets developers, QA engineers, and DevOps teams test how their REST APIs perform under real-world traffic without writing scripts, managing infrastructure, or installing software. You simply paste a cURL command or import a Postman collection, choose a load profile, and get detailed performance results in under 60 seconds.'
            },
            {
                q: 'How do I load test a REST API without JMeter?',
                a: 'With APILoadTest, you can load test any REST API without JMeter by simply pasting a cURL command or importing a Postman collection into the platform. No XML configuration, no test plans, no plugins required. Just sign up, purchase a credit pack, paste your API details, select the number of virtual users and duration, and click Run. You\'ll get p90/p95/p99 latency metrics, throughput, and error rate data in under 60 seconds.'
            },
            {
                q: 'How does pricing work?',
                a: 'APILoadTest uses a pay-as-you-go credit pack model — no subscriptions. Credit packs start at $4.99 for 5 credits. Each credit equals one load test run, and credits never expire.'
            },
            {
                q: 'How long does it take to run a load test?',
                a: 'From signup to your first test result takes under 60 seconds. The actual test duration is configurable — you can run tests from a few seconds up to 5 minutes depending on your plan. Results are displayed in real-time as the test executes.'
            },
        ]
    },
    {
        category: 'Features & Capabilities',
        questions: [
            {
                q: 'What load profiles does APILoadTest support?',
                a: 'APILoadTest supports four load profiles: (1) Fixed — constant number of virtual users for baseline performance testing, (2) Ramp Up — gradually increasing VUs to find breaking points, (3) Spike — sudden burst of traffic to simulate viral moments or flash sales, and (4) Peak — high VU count sustained over time to test under heavy, sustained load.'
            },
            {
                q: 'What metrics does APILoadTest provide?',
                a: 'APILoadTest provides comprehensive performance metrics including: total requests, requests per second (throughput), average response time, minimum and maximum response times, percentile response times (p50, p90, p95, p99), error rate percentage, success rate, status code distribution (200, 4xx, 5xx breakdown), and real-time response time charts during test execution.'
            },
            {
                q: 'Can I import Postman collections?',
                a: 'Yes! You can import entire Postman collections with all requests, headers, authentication, and body data pre-configured. APILoadTest parses the collection and sets up load tests automatically — no manual configuration needed.'
            },
            {
                q: 'Does APILoadTest support authenticated APIs?',
                a: 'Yes. APILoadTest fully supports Bearer tokens, API keys, and custom headers. You can test any protected endpoint exactly as your real users would access it, including OAuth 2.0 Bearer tokens, API key authentication, and any custom header-based auth scheme.'
            },
        ]
    },
    {
        category: 'Technical Questions',
        questions: [
            {
                q: 'What is p99 latency and why does it matter?',
                a: 'P99 latency (99th percentile response time) means that 99% of all requests completed faster than this value. It captures the experience of your slowest 1% of users. This metric matters because averages can hide outliers — your average might be 50ms while 1 in 100 users experiences 5-second delays. P99 latency reveals these tail-end performance issues that directly impact user satisfaction.'
            },
            {
                q: 'What are Virtual Users (VUs) in load testing?',
                a: 'Virtual Users (VUs) are simulated concurrent users that send requests to your API simultaneously. If you set 50 VUs, the load testing tool generates traffic equivalent to 50 users all hitting your API at the same time. VUs keep sending requests throughout the test duration, so 50 VUs running for 60 seconds will generate far more than 50 requests — the exact number depends on your API\'s response time.'
            },
            {
                q: 'What is ramp-up testing?',
                a: 'Ramp-up testing is a load profile where the number of virtual users gradually increases over the test duration rather than starting at full capacity. This helps you identify the exact point where your API\'s performance begins to degrade — for example, response times may be stable at 20 VUs, start growing at 50 VUs, and spike at 100 VUs. It\'s the most popular profile for capacity planning.'
            },
            {
                q: 'Does APILoadTest have an API?',
                a: 'Yes! APILoadTest provides a REST API that lets you run load tests programmatically. You can authenticate with an API key (format: alt_live_...) and submit test configurations as JSON. This enables integration into CI/CD pipelines, automated testing workflows, and custom tooling. See our API documentation at apiloadtest.com/api-docs.'
            },
        ]
    },
    {
        category: 'Pricing & Credits',
        questions: [
            {
                q: 'How does the credit system work?',
                a: 'APILoadTest uses a simple pay-as-you-go credit pack model. 1 credit = 1 load test run. You buy credit packs once — they never expire and there are no subscriptions. Packs available: Starter ($4.99, 5 credits), Builder ($19.99, 20 credits), Pro ($49.99, 50 credits), and Team ($199.99, 200 credits).'
            },
            {
                q: 'Do credits expire?',
                a: 'No. Credits never expire. Once purchased, they remain in your account indefinitely until you use them. There are no monthly fees, no subscriptions, and no time limits.'
            },
        ]
    },
    {
        category: 'Comparisons',
        questions: [
            {
                q: 'How is APILoadTest different from JMeter?',
                a: 'JMeter requires local installation, complex XML-based test plan configuration, plugin management, and scripting knowledge. APILoadTest is entirely cloud-based with zero setup — just paste a cURL or import a Postman collection and run. JMeter takes hours to configure; APILoadTest gives you results in 60 seconds.'
            },
            {
                q: 'How is APILoadTest different from k6?',
                a: 'k6 requires writing test scripts in JavaScript and managing your own infrastructure for cloud execution. APILoadTest requires zero code — you configure everything through a visual interface or by pasting a cURL command. k6 is powerful for scriptable scenarios, but for straightforward API load testing, APILoadTest gives you results in a fraction of the time with no learning curve.'
            },
            {
                q: 'How is APILoadTest different from Gatling?',
                a: 'Gatling requires writing test simulations in Scala (or Java/Kotlin), learning its DSL, and managing infrastructure. APILoadTest requires no code at all. For developers who need quick API performance insights without investing time in learning a testing framework, APILoadTest delivers results in 60 seconds with zero setup.'
            },
        ]
    }
];

const FAQPage = () => {
    const navigate = useNavigate();

    const faqSchema = {
        "@context": "https://schema.org",
        "@type": "FAQPage",
        "mainEntity": faqData.flatMap(category =>
            category.questions.map(item => ({
                "@type": "Question",
                "name": item.q,
                "acceptedAnswer": {
                    "@type": "Answer",
                    "text": item.a
                }
            }))
        )
    };

    const breadcrumbSchema = {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        "itemListElement": [
            { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://www.apiloadtest.com" },
            { "@type": "ListItem", "position": 2, "name": "FAQ", "item": "https://www.apiloadtest.com/faq" }
        ]
    };

    return (
        <div style={styles.page}>
            <Helmet>
                <title>FAQ - Frequently Asked Questions | APILoadTest.com</title>
                <meta name="description" content="Frequently asked questions about APILoadTest — no-code API load testing platform. Learn about load profiles, virtual users, p99 latency, pricing, and how to test APIs without JMeter." />
                <meta name="keywords" content="API load testing FAQ, load testing questions, p99 latency, virtual users, JMeter alternative FAQ, no-code load testing, API performance testing FAQ" />
                <link rel="canonical" href="https://www.apiloadtest.com/faq" />
                <meta property="og:title" content="FAQ - APILoadTest.com" />
                <meta property="og:description" content="Answers to common questions about API load testing, performance metrics, and using APILoadTest." />
                <meta property="og:url" content="https://www.apiloadtest.com/faq" />
                <meta property="og:type" content="website" />
                <meta name="robots" content="index, follow" />
                <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>
                <script type="application/ld+json">{JSON.stringify(breadcrumbSchema)}</script>
            </Helmet>

            {/* Header */}
            <header style={styles.header}>
                <div style={styles.headerInner}>
                    <Link to="/" style={styles.logoSection}>
                        <img src="/logo.png" alt="APILoadTest.com" style={styles.logoImage} />
                        <span style={styles.logoText}>APILoadTest.com</span>
                    </Link>
                    <nav style={styles.navLinks}>
                        <Link to="/#features" style={styles.navLink}>Features</Link>
                        <Link to="/#pricing" style={styles.navLink}>Pricing</Link>
                        <Link to="/blog" style={styles.navLink}>Blog</Link>
                        <Link to="/glossary" style={styles.navLink}>Glossary</Link>
                    </nav>
                    <button style={styles.cta} onClick={() => navigate('/login')}>Get Started</button>
                </div>
            </header>

            {/* Breadcrumb */}
            <nav style={styles.breadcrumb} aria-label="Breadcrumb">
                <Link to="/" style={styles.breadcrumbLink}>Home</Link>
                <span style={styles.sep}>/</span>
                <span style={styles.breadcrumbCurrent}>FAQ</span>
            </nav>

            {/* Hero */}
            <section style={styles.hero}>
                <h1 style={styles.heroTitle}>Frequently Asked Questions</h1>
                <p style={styles.heroSub}>Everything you need to know about API load testing with APILoadTest.</p>
            </section>

            {/* FAQ Content */}
            <main style={styles.main}>
                {faqData.map((category, catIdx) => (
                    <section key={catIdx} style={styles.categorySection}>
                        <h2 style={styles.categoryTitle}>{category.category}</h2>
                        <div style={styles.questionsContainer}>
                            {category.questions.map((item, qIdx) => (
                                <details key={qIdx} style={styles.faqItem}>
                                    <summary style={styles.question}>{item.q}</summary>
                                    <p style={styles.answer}>{item.a}</p>
                                </details>
                            ))}
                        </div>
                    </section>
                ))}
            </main>

            {/* CTA */}
            <section style={styles.ctaSection}>
                <h2 style={styles.ctaTitle}>Still have questions?</h2>
                <p style={styles.ctaSub}>Run your first API load test in under 60 seconds.</p>
                <button style={styles.ctaButton} onClick={() => navigate('/login')}>Get Started →</button>
            </section>

            {/* Footer */}
            <footer style={styles.footer}>
                <div style={styles.footerInner}>
                    <div style={styles.footerLinks}>
                        <a href="/terms" style={styles.footerLink}>Terms</a>
                        <a href="/privacy" style={styles.footerLink}>Privacy</a>
                        <a href="/refund" style={styles.footerLink}>Refund</a>
                        <a href="/faq" style={{ ...styles.footerLink, color: '#6366f1' }}>FAQ</a>
                        <a href="/glossary" style={styles.footerLink}>Glossary</a>
                        <a href="mailto:admin@apiloadtest.com" style={styles.footerLink}>Contact</a>
                    </div>
                    <p style={styles.copyright}>© 2026 APILoadTest.com · All rights reserved</p>
                </div>
            </footer>

            <style>{`
                details[style] summary::-webkit-details-marker { display: none; }
                details summary { list-style: none; }
                details summary::before {
                    content: '+';
                    font-size: 20px;
                    font-weight: 700;
                    color: #6366f1;
                    margin-right: 16px;
                    transition: transform 0.2s;
                    display: inline-block;
                    min-width: 20px;
                }
                details[open] summary::before {
                    content: '−';
                    color: #6366f1;
                }
                details[open] p {
                    animation: faqFadeIn 0.3s ease;
                }
                @keyframes faqFadeIn {
                    from { opacity: 0; transform: translateY(-8px); }
                    to { opacity: 1; transform: translateY(0); }
                }
                @media (max-width: 768px) {
                    .faq-nav-links { display: none !important; }
                }
            `}</style>
        </div>
    );
};

const styles = {
    page: { minHeight: '100vh', background: '#F7F8FC', fontFamily: "'DM Sans', 'Inter', sans-serif" },
    header: { background: '#fff', padding: '16px 0', position: 'sticky', top: 0, zIndex: 100, borderBottom: '1px solid #E4E5EF' },
    headerInner: { maxWidth: '1200px', margin: '0 auto', padding: '0 24px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' },
    logoSection: { display: 'flex', alignItems: 'center', gap: '12px', textDecoration: 'none' },
    logoImage: { height: '40px', width: 'auto' },
    logoText: { fontFamily: "'Fraunces', serif", fontSize: '20px', fontWeight: '700', color: '#0A0D1C' },
    navLinks: { display: 'flex', gap: '32px', className: 'faq-nav-links' },
    navLink: { color: '#3D3F52', textDecoration: 'none', fontSize: '15px', fontWeight: '500' },
    cta: { background: '#6366f1', color: '#fff', border: 'none', padding: '12px 28px', borderRadius: '24px', fontSize: '15px', fontWeight: '600', cursor: 'pointer' },
    breadcrumb: { maxWidth: '900px', margin: '0 auto', padding: '24px', fontSize: '14px' },
    breadcrumbLink: { color: '#6366f1', textDecoration: 'none' },
    sep: { margin: '0 8px', color: '#8B8FA8' },
    breadcrumbCurrent: { color: '#8B8FA8' },
    hero: { textAlign: 'center', padding: '40px 24px 48px', maxWidth: '700px', margin: '0 auto' },
    heroTitle: { fontFamily: "'Fraunces', serif", fontSize: '48px', fontWeight: '900', color: '#0A0D1C', letterSpacing: '-2px', marginBottom: '16px' },
    heroSub: { fontSize: '18px', color: '#8B8FA8', lineHeight: '1.6' },
    main: { maxWidth: '900px', margin: '0 auto', padding: '0 24px 64px' },
    categorySection: { marginBottom: '48px' },
    categoryTitle: { fontFamily: "'Fraunces', serif", fontSize: '24px', fontWeight: '700', color: '#0A0D1C', marginBottom: '20px', paddingBottom: '12px', borderBottom: '2px solid #E4E5EF' },
    questionsContainer: { display: 'flex', flexDirection: 'column', gap: '0' },
    faqItem: { background: '#fff', borderBottom: '1px solid #E4E5EF', cursor: 'pointer' },
    question: { padding: '20px 24px', fontSize: '17px', fontWeight: '600', color: '#0A0D1C', cursor: 'pointer', display: 'flex', alignItems: 'center' },
    answer: { padding: '0 24px 20px 60px', fontSize: '16px', lineHeight: '1.8', color: '#3D3F52', margin: 0 },
    ctaSection: { background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)', padding: '80px 24px', textAlign: 'center' },
    ctaTitle: { fontSize: '36px', fontWeight: '700', color: '#fff', marginBottom: '16px', fontFamily: "'Fraunces', serif" },
    ctaSub: { fontSize: '18px', color: 'rgba(255,255,255,.9)', marginBottom: '32px' },
    ctaButton: { background: '#fff', color: '#6366f1', border: 'none', padding: '16px 40px', borderRadius: '30px', fontSize: '16px', fontWeight: '700', cursor: 'pointer' },
    footer: { background: '#fff', padding: '40px 24px', borderTop: '1px solid #E4E5EF' },
    footerInner: { maxWidth: '1200px', margin: '0 auto', textAlign: 'center' },
    footerLinks: { display: 'flex', gap: '24px', justifyContent: 'center', marginBottom: '16px' },
    footerLink: { color: '#8B8FA8', textDecoration: 'none', fontSize: '14px' },
    copyright: { fontSize: '14px', color: '#8B8FA8' },
};

export default FAQPage;
